import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const testimonials = [
  {
    name: 'Michael Rodriguez',
    role: 'Real Estate Investor',
    location: 'Phoenix, AZ',
    rating: 5,
    text: 'DSCR loans changed my investment strategy completely. I was able to finance 3 properties in 6 months without providing any tax returns. The process was incredibly smooth and fast.',
    properties: '12 Properties',
    avatar: 'https://public.readdy.ai/ai/img_res/57be88684935e93952175903032a7d52.jpg'
  },
  {
    name: 'Sarah Chen',
    role: 'Property Portfolio Manager',
    location: 'Austin, TX',
    rating: 5,
    text: 'As a self-employed investor, traditional mortgages were a nightmare. DSCR loans made it possible to grow my portfolio without the hassle of income verification. Best decision I ever made.',
    properties: '8 Properties',
    avatar: 'https://readdy.ai/api/search-image?query=professional%20confident%20asian%20female%20real%20estate%20investor%20businesswoman%20in%20elegant%20business%20attire%20with%20warm%20smile%20and%20trustworthy%20appearance%20in%20natural%20office%20lighting%20high%20quality%20portrait%20photography&width=200&height=200&seq=avatar-002&orientation=squarish'
  },
  {
    name: 'David Thompson',
    role: 'Multi-Family Investor',
    location: 'Denver, CO',
    rating: 5,
    text: 'I closed on a 4-unit property in just 18 days with a DSCR loan. The competitive rates and no income documentation requirement made this the easiest real estate transaction I\'ve ever done.',
    properties: '15 Properties',
    avatar: 'https://readdy.ai/api/search-image?query=professional%20confident%20caucasian%20male%20real%20estate%20investor%20businessman%20in%20modern%20business%20suit%20with%20warm%20smile%20and%20trustworthy%20appearance%20in%20natural%20office%20lighting%20high%20quality%20portrait%20photography&width=200&height=200&seq=avatar-003&orientation=squarish'
  },
  {
    name: 'Jennifer Martinez',
    role: 'Short-Term Rental Investor',
    location: 'Miami, FL',
    rating: 5,
    text: 'DSCR loans are perfect for Airbnb investors like me. The lender understood my business model and approved my loan based on the property\'s rental potential. Highly recommend!',
    properties: '6 Properties',
    avatar: 'https://readdy.ai/api/search-image?query=professional%20confident%20latina%20female%20real%20estate%20investor%20businesswoman%20in%20elegant%20business%20attire%20with%20warm%20smile%20and%20trustworthy%20appearance%20in%20natural%20office%20lighting%20high%20quality%20portrait%20photography&width=200&height=200&seq=avatar-004&orientation=squarish'
  },
  {
    name: 'Robert Kim',
    role: 'Commercial Property Investor',
    location: 'Seattle, WA',
    rating: 5,
    text: 'After being turned down by traditional lenders, DSCR loans opened doors I thought were closed. I financed 5 properties in one year and my portfolio has never been stronger.',
    properties: '20 Properties',
    avatar: 'https://readdy.ai/api/search-image?query=professional%20confident%20asian%20male%20real%20estate%20investor%20businessman%20in%20modern%20business%20suit%20with%20warm%20smile%20and%20trustworthy%20appearance%20in%20natural%20office%20lighting%20high%20quality%20portrait%20photography&width=200&height=200&seq=avatar-005&orientation=squarish'
  }
];

const slideVariants = {
  enter: (direction: number) => ({
    x: direction > 0 ? 1000 : -1000,
    opacity: 0,
    scale: 0.8
  }),
  center: {
    zIndex: 1,
    x: 0,
    opacity: 1,
    scale: 1
  },
  exit: (direction: number) => ({
    zIndex: 0,
    x: direction < 0 ? 1000 : -1000,
    opacity: 0,
    scale: 0.8
  })
};

export default function TestimonialsSlider() {
  const [[page, direction], setPage] = useState([0, 0]);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  const testimonialIndex = ((page % testimonials.length) + testimonials.length) % testimonials.length;

  const paginate = (newDirection: number) => {
    setPage([page + newDirection, newDirection]);
  };

  useEffect(() => {
    if (!isAutoPlaying) return;
    
    const timer = setInterval(() => {
      paginate(1);
    }, 5000);

    return () => clearInterval(timer);
  }, [page, isAutoPlaying]);

  return (
    <section className="py-20 bg-gradient-to-b from-white to-slate-50 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
            What Our Clients Say
          </h2>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            Join thousands of successful real estate investors who have grown their portfolios with DSCR loans
          </p>
        </motion.div>

        <div className="relative max-w-5xl mx-auto">
          {/* Testimonial Card */}
          <div 
            className="relative h-[500px] flex items-center justify-center"
            onMouseEnter={() => setIsAutoPlaying(false)}
            onMouseLeave={() => setIsAutoPlaying(true)}
          >
            <AnimatePresence initial={false} custom={direction} mode="wait">
              <motion.div
                key={page}
                custom={direction}
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{
                  x: { type: "spring", stiffness: 300, damping: 30 },
                  opacity: { duration: 0.3 },
                  scale: { duration: 0.3 }
                }}
                className="absolute w-full"
              >
                <div className="bg-white rounded-3xl shadow-2xl p-8 md:p-12 border border-slate-200">
                  <div className="flex flex-col md:flex-row items-center gap-8">
                    {/* Avatar */}
                    <motion.div
                      initial={{ scale: 0.8, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      transition={{ duration: 0.5, ease: "easeOut", delay: 0.2 }}
                      className="flex-shrink-0"
                    >
                      <div className="relative">
                        <div className="w-32 h-32 rounded-full overflow-hidden border-4 border-blue-600 shadow-xl">
                          <img
                            src={testimonials[testimonialIndex].avatar}
                            alt={testimonials[testimonialIndex].name}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <motion.div
                          initial={{ scale: 0, opacity: 0 }}
                          animate={{ scale: 1, opacity: 1 }}
                          transition={{ delay: 0.5, duration: 0.4, ease: "easeOut" }}
                          className="absolute -bottom-2 -right-2 bg-gradient-to-r from-amber-500 to-amber-600 text-white px-3 py-1 rounded-full text-sm font-bold shadow-lg"
                        >
                          {testimonials[testimonialIndex].properties}
                        </motion.div>
                      </div>
                    </motion.div>

                    {/* Content */}
                    <div className="flex-1 text-center md:text-left">
                      {/* Stars */}
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.3 }}
                        className="flex justify-center md:justify-start gap-1 mb-4"
                      >
                        {[...Array(testimonials[testimonialIndex].rating)].map((_, i) => (
                          <motion.i
                            key={i}
                            initial={{ scale: 0, rotate: -180 }}
                            animate={{ scale: 1, rotate: 0 }}
                            transition={{ delay: 0.4 + i * 0.1, type: "spring", stiffness: 200 }}
                            className="ri-star-fill text-2xl text-amber-500"
                          ></motion.i>
                        ))}
                      </motion.div>

                      {/* Quote */}
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.5 }}
                        className="relative"
                      >
                        <i className="ri-double-quotes-l text-5xl text-blue-200 absolute -top-4 -left-2 md:-left-6"></i>
                        <p className="text-lg md:text-xl text-slate-700 leading-relaxed mb-6 relative z-10 italic">
                          {testimonials[testimonialIndex].text}
                        </p>
                        <i className="ri-double-quotes-r text-5xl text-blue-200 absolute -bottom-4 -right-2 md:-right-6"></i>
                      </motion.div>

                      {/* Author */}
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.6 }}
                      >
                        <div className="font-bold text-xl text-slate-900">
                          {testimonials[testimonialIndex].name}
                        </div>
                        <div className="text-blue-600 font-semibold">
                          {testimonials[testimonialIndex].role}
                        </div>
                        <div className="text-slate-500 text-sm flex items-center justify-center md:justify-start mt-1">
                          <i className="ri-map-pin-line mr-1"></i>
                          {testimonials[testimonialIndex].location}
                        </div>
                      </motion.div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Navigation Buttons */}
          <div className="flex justify-center items-center gap-4 mt-8">
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={() => paginate(-1)}
              className="w-12 h-12 rounded-full bg-white border-2 border-blue-600 text-blue-600 hover:bg-blue-600 hover:text-white transition-all shadow-lg flex items-center justify-center"
            >
              <i className="ri-arrow-left-line text-xl"></i>
            </motion.button>

            {/* Dots */}
            <div className="flex gap-2">
              {testimonials.map((_, index) => (
                <motion.button
                  key={index}
                  whileHover={{ scale: 1.2 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={() => setPage([index, index > testimonialIndex ? 1 : -1])}
                  className={`h-3 rounded-full transition-all ${
                    index === testimonialIndex
                      ? 'w-8 bg-gradient-to-r from-blue-600 to-amber-500'
                      : 'w-3 bg-slate-300 hover:bg-slate-400'
                  }`}
                />
              ))}
            </div>

            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={() => paginate(1)}
              className="w-12 h-12 rounded-full bg-white border-2 border-blue-600 text-blue-600 hover:bg-blue-600 hover:text-white transition-all shadow-lg flex items-center justify-center"
            >
              <i className="ri-arrow-right-line text-xl"></i>
            </motion.button>
          </div>

          {/* Auto-play indicator */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center mt-4"
          >
            <span className="text-sm text-slate-500">
              {isAutoPlaying ? (
                <><i className="ri-play-circle-line mr-1"></i>Auto-playing</>
              ) : (
                <><i className="ri-pause-circle-line mr-1"></i>Paused</>
              )}
            </span>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
